// __DASH_COCKPIT_v1__
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import {
  Users, AlertCircle, CheckCircle, ListTodo, Clock,
  BarChart2, IndianRupee, TrendingUp, Trophy, XCircle,
  PhoneCall, Percent, ChevronRight, UserCheck, UserX,
  UserMinus, CalendarClock, Timer, Sparkles, ArrowUpRight,
  User as UserIcon, ClipboardList, Activity, Crown, Medal
} from "lucide-react";
import { ResponsiveContainer, AreaChart, Area } from "recharts";

// ============================================================
// KpiCard — compact tinted KPI tile with delta pill + sparkline
// ============================================================
const KpiCard = React.memo(({ icon: Icon, label, value, delta, deltaSuffix = "%", series, seriesKey = "leads", color = "indigo", format = "number" }) => {
  const colorMap = {
    indigo:  { text: "text-indigo-700",  iconBg: "bg-indigo-600",  cardBg: "bg-gradient-to-br from-indigo-50 via-white to-indigo-50/40 border-indigo-200/60",  stroke: "#6366f1" },
    emerald: { text: "text-emerald-700", iconBg: "bg-emerald-600", cardBg: "bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40 border-emerald-200/60", stroke: "#10b981" },
    amber:   { text: "text-amber-700",   iconBg: "bg-amber-500",   cardBg: "bg-gradient-to-br from-amber-50 via-white to-amber-50/40 border-amber-200/60",     stroke: "#f59e0b" },
    rose:    { text: "text-rose-700",    iconBg: "bg-rose-500",    cardBg: "bg-gradient-to-br from-rose-50 via-white to-rose-50/40 border-rose-200/60",        stroke: "#f43f5e" },
    violet:  { text: "text-violet-700",  iconBg: "bg-violet-600",  cardBg: "bg-gradient-to-br from-violet-50 via-white to-violet-50/40 border-violet-200/60",  stroke: "#8b5cf6" },
    sky:     { text: "text-sky-700",     iconBg: "bg-sky-500",     cardBg: "bg-gradient-to-br from-sky-50 via-white to-sky-50/40 border-sky-200/60",           stroke: "#0ea5e9" },
  };
  const c = colorMap[color] || colorMap.indigo;
  const isPositive = (delta ?? 0) >= 0;
  const isZero = (delta ?? 0) === 0;
  const deltaColor = isZero ? "text-slate-500 bg-white/80" : isPositive ? "text-emerald-700 bg-white/80" : "text-rose-700 bg-white/80";
  const formatVal = (v) => {
    if (format === "currency") {
      if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
      if (v >= 100000)   return `₹${(v / 100000).toFixed(2)}L`;
      if (v >= 1000)     return `₹${(v / 1000).toFixed(1)}K`;
      return `₹${(v || 0).toLocaleString("en-IN")}`;
    }
    if (format === "percent") return `${v || 0}%`;
    return (v || 0).toLocaleString("en-IN");
  };
  return (
    <Card className={`relative ${c.cardBg} dark:bg-slate-800 rounded-xl border shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all`}>
      <CardContent className="p-3">
        <div className="flex items-center gap-2 mb-1.5">
          {Icon && (
            <div className={`w-7 h-7 ${c.iconBg} rounded-lg flex items-center justify-center shadow-sm`}>
              <Icon className="w-3.5 h-3.5 text-white" />
            </div>
          )}
          <p className={`text-[10px] font-semibold ${c.text} uppercase tracking-wider truncate`}>{label}</p>
        </div>
        <div className="flex items-baseline gap-1.5 flex-wrap">
          <h3 className="text-lg font-bold text-slate-900 dark:text-white leading-tight">{formatVal(value)}</h3>
          {delta !== undefined && delta !== null && (
            <span className={`text-[9px] font-semibold px-1 py-0.5 rounded-full ${deltaColor} whitespace-nowrap`}>
              {isPositive && !isZero ? "+" : ""}{delta}{deltaSuffix}
            </span>
          )}
        </div>
        {Array.isArray(series) && series.length > 1 && (
          <div className="mt-1 -mx-1 h-6">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id={`grad-${label.replace(/\s+/g, "-")}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={c.stroke} stopOpacity={0.4} />
                    <stop offset="95%" stopColor={c.stroke} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey={seriesKey} stroke={c.stroke} strokeWidth={1.5} fill={`url(#grad-${label.replace(/\s+/g, "-")})`} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
});

// ============================================================
// StatRow — label left, bold value right
// ============================================================
const StatRow = React.memo(({ label, value, valueClass = "text-slate-900 dark:text-white" }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className="text-[13px] text-slate-500 dark:text-slate-400">{label}</span>
    <span className={`text-[18px] font-bold ${valueClass}`}>{value}</span>
  </div>
));

// ============================================================
// SummaryCard — shell for Attendance / Leads / Tasks blocks
// ============================================================
const SummaryCard = React.memo(({ icon: Icon, title, iconColor = "indigo", iconBg = "bg-indigo-100", iconFg = "text-indigo-600", children, footerLabel, onFooterClick }) => (
  <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-[0_1px_3px_rgb(0,0,0,0.04)] p-5 flex flex-col">
    <div className="flex items-center gap-2 mb-3">
      <div className={`w-10 h-10 rounded-xl ${iconBg} dark:bg-slate-700 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 ${iconFg}`} />
      </div>
      <h3 className="text-[11px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400">{title}</h3>
    </div>
    <div className="flex-1 divide-y divide-slate-100 dark:divide-slate-700/50">
      {children}
    </div>
    {footerLabel && (
      <button
        type="button"
        onClick={onFooterClick}
        className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-700 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 flex items-center gap-1 transition-colors self-start"
      >
        {footerLabel}
        <ChevronRight className="w-3 h-3" />
      </button>
    )}
  </div>
));

// ============================================================
// AttendanceCard
// ============================================================
const AttendanceCard = React.memo(({ summary, onOpen }) => {
  const s = summary || {};
  return (
    <SummaryCard
      icon={UserCheck}
      title="Attendance"
      iconBg="bg-sky-100"
      iconFg="text-sky-600"
      footerLabel="View records"
      onFooterClick={onOpen}
    >
      <StatRow label="Total staff" value={s.total_staff ?? 0} />
      <StatRow label="Present" value={s.present ?? 0} valueClass="text-emerald-600" />
      <StatRow label="Absent" value={s.absent ?? 0} valueClass="text-rose-600" />
      <StatRow label="Late" value={s.late ?? 0} valueClass="text-amber-600" />
      <StatRow label="Half-day" value={s.half_day ?? 0} valueClass="text-violet-600" />
    </SummaryCard>
  );
});

// ============================================================
// LeadsCard
// ============================================================
const LeadsCard = React.memo(({ metrics, onOpen }) => {
  const m = metrics || {};
  const series = Array.isArray(m.sparkline) ? m.sparkline : [];
  return (
    <SummaryCard
      icon={Users}
      title="Leads"
      iconBg="bg-indigo-100"
      iconFg="text-indigo-600"
      footerLabel="Open leads"
      onFooterClick={onOpen}
    >
      <StatRow label="New today" value={m.new_today ?? m.new_leads_today ?? 0} valueClass="text-indigo-600" />
      <StatRow label="Assigned" value={m.assigned_leads ?? 0} />
      <StatRow label="Unassigned" value={m.unassigned_leads ?? 0} valueClass="text-amber-600" />
      <StatRow label="Follow-ups" value={m.followup_pending ?? 0} valueClass="text-rose-600" />
      {series.length > 1 && (
        <div className="pt-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] text-slate-400 uppercase tracking-wider">7-day trend</span>
          </div>
          <div className="h-8">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="grad-leads-spark" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="leads" stroke="#6366f1" strokeWidth={1.5} fill="url(#grad-leads-spark)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </SummaryCard>
  );
});

// ============================================================
// TasksCard
// ============================================================
const TasksCard = React.memo(({ summary, dueToday, overdue, onOpen }) => {
  const t = summary || {};
  return (
    <SummaryCard
      icon={ListTodo}
      title="Tasks"
      iconBg="bg-violet-100"
      iconFg="text-violet-600"
      footerLabel="Open tasks"
      onFooterClick={onOpen}
    >
      <StatRow label="Open" value={t.total_pending ?? 0} />
      <StatRow label="In progress" value={t.total_in_progress ?? 0} valueClass="text-sky-600" />
      <StatRow label="Due today" value={dueToday ?? 0} valueClass="text-amber-600" />
      <StatRow label="Overdue" value={overdue ?? 0} valueClass="text-rose-600" />
    </SummaryCard>
  );
});

// ============================================================
// SalesTeamPerformance — table with sort + mini bar
// ============================================================
const SalesTeamPerformance = React.memo(({ rows, onRowClick }) => {
  const [sortBy, setSortBy] = useState("revenue");
  const sorted = useMemo(() => {
    const list = [...(rows || [])];
    list.sort((a, b) => {
      switch (sortBy) {
        case "leads":       return (b.total_leads || 0) - (a.total_leads || 0);
        case "conversion":  return (b.conversion_rate || 0) - (a.conversion_rate || 0);
        case "activity":    return (b.followup_efficiency || 0) - (a.followup_efficiency || 0);
        case "revenue":
        default:            return (b.revenue || 0) - (a.revenue || 0);
      }
    });
    return list;
  }, [rows, sortBy]);

  const maxRevenue = useMemo(() => sorted.reduce((m, r) => Math.max(m, r.revenue || 0), 0) || 1, [sorted]);

  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)}Cr`;
    if (value >= 100000)   return `₹${(value / 100000).toFixed(2)}L`;
    if (value >= 1000)     return `₹${(value / 1000).toFixed(1)}K`;
    return `₹${(value || 0).toLocaleString("en-IN")}`;
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-[0_1px_3px_rgb(0,0,0,0.04)] p-5 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-slate-700 flex items-center justify-center">
            <BarChart2 className="w-5 h-5 text-emerald-600" />
          </div>
          <h3 className="text-[11px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400">Sales Team Performance</h3>
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="text-xs bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-1 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
        >
          <option value="revenue">Sort: Revenue</option>
          <option value="leads">Sort: Leads</option>
          <option value="conversion">Sort: Conversion</option>
          <option value="activity">Sort: Activity</option>
        </select>
      </div>

      {sorted.length === 0 ? (
        <div className="text-center py-8 text-sm text-slate-400">No sales data yet</div>
      ) : (
        <div className="overflow-x-auto">
          <div className="max-h-[320px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white dark:bg-slate-800">
                <tr className="text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100 dark:border-slate-700">
                  <th className="text-left py-2 pr-2 font-semibold">Member</th>
                  <th className="text-center py-2 px-2 font-semibold">Leads</th>
                  <th className="text-center py-2 px-2 font-semibold">Won</th>
                  <th className="text-center py-2 px-2 font-semibold">Conv%</th>
                  <th className="text-right py-2 px-2 font-semibold">Revenue</th>
                  <th className="text-left py-2 pl-2 font-semibold w-24">Trend</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((m, i) => {
                  const bgColors = ["bg-indigo-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500", "bg-violet-500"];
                  const bgColor = bgColors[i % bgColors.length];
                  const barPct = Math.max(4, Math.round(((m.revenue || 0) / maxRevenue) * 100));
                  return (
                    <tr
                      key={m.user_id || i}
                      onClick={() => onRowClick && onRowClick(m)}
                      className="border-b border-slate-100 dark:border-slate-700/70 hover:bg-slate-50 dark:hover:bg-slate-700/40 cursor-pointer transition-colors"
                    >
                      <td className="py-2 pr-2">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center overflow-hidden ${bgColor}`}>
                            {m.picture ? (
                              <img src={m.picture} alt={m.name} className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-white text-xs font-bold">{(m.name || "?").charAt(0).toUpperCase()}</span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 dark:text-white truncate">{m.name}</p>
                            <p className="text-[11px] text-slate-400 truncate">{m.role}</p>
                          </div>
                        </div>
                      </td>
                      <td className="text-center py-2 px-2 font-semibold text-slate-800 dark:text-slate-100">{m.total_leads || 0}</td>
                      <td className="text-center py-2 px-2 font-semibold text-emerald-600">{m.converted || 0}</td>
                      <td className={`text-center py-2 px-2 font-bold ${
                        (m.conversion_rate || 0) >= 50 ? "text-emerald-600" :
                        (m.conversion_rate || 0) >= 25 ? "text-amber-600" : "text-rose-600"
                      }`}>{m.conversion_rate || 0}%</td>
                      <td className="text-right py-2 px-2 font-bold text-slate-900 dark:text-white">{formatCurrency(m.revenue || 0)}</td>
                      <td className="py-2 pl-2">
                        <div className="h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full" style={{ width: `${barPct}%` }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
});

// ============================================================
// TeamLeaderboardCard
// ============================================================
const TeamLeaderboardCard = React.memo(({ rows, onOpen }) => {
  const list = Array.isArray(rows) ? rows.slice(0, 8) : [];
  const rankBadge = (i) => {
    if (i === 0) return <span className="text-lg">🥇</span>;
    if (i === 1) return <span className="text-lg">🥈</span>;
    if (i === 2) return <span className="text-lg">🥉</span>;
    return <span className="text-xs font-bold text-slate-500">#{i + 1}</span>;
  };
  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-[0_1px_3px_rgb(0,0,0,0.04)] p-5 flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-slate-700 flex items-center justify-center">
          <Trophy className="w-5 h-5 text-amber-600" />
        </div>
        <h3 className="text-[11px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400">Team Leaderboard</h3>
      </div>
      {list.length === 0 ? (
        <div className="flex-1 flex items-center justify-center py-8 text-sm text-slate-400">No ranking data</div>
      ) : (
        <ol className="space-y-2 flex-1">
          {list.map((r, i) => (
            <li key={r.user_id || i} className="flex items-center gap-3 py-1.5">
              <div className="w-8 flex items-center justify-center">{rankBadge(i)}</div>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center overflow-hidden flex-shrink-0">
                {r.picture ? (
                  <img src={r.picture} alt={r.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white text-xs font-bold">{(r.name || "?").charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{r.name}</p>
                <p className="text-[11px] text-slate-400 truncate">{r.role || r.level || ""}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-slate-900 dark:text-white">{r.score ?? r.performance_score ?? 0}</p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">score</p>
              </div>
            </li>
          ))}
        </ol>
      )}
      <button
        type="button"
        onClick={onOpen}
        className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-700 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 flex items-center gap-1 transition-colors self-start"
      >
        Full leaderboard <ChevronRight className="w-3 h-3" />
      </button>
    </div>
  );
});

// ============================================================
// RecentActivityFeed
// ============================================================
const RecentActivityFeed = React.memo(({ events, onEventClick }) => {
  const list = Array.isArray(events) ? events.slice(0, 5) : [];

  const iconMap = {
    new_lead:       { Icon: Users,       bg: "bg-indigo-100 dark:bg-indigo-900/30", fg: "text-indigo-600 dark:text-indigo-400" },
    task_completed: { Icon: CheckCircle, bg: "bg-emerald-100 dark:bg-emerald-900/30", fg: "text-emerald-600 dark:text-emerald-400" },
    task_assigned:  { Icon: UserIcon,    bg: "bg-violet-100 dark:bg-violet-900/30", fg: "text-violet-600 dark:text-violet-400" },
    attendance_in:  { Icon: Clock,       bg: "bg-sky-100 dark:bg-sky-900/30", fg: "text-sky-600 dark:text-sky-400" },
  };

  const relTime = (ts) => {
    if (!ts) return "";
    const t = new Date(ts).getTime();
    if (Number.isNaN(t)) return "";
    const diff = Math.max(0, Date.now() - t);
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-[0_1px_3px_rgb(0,0,0,0.04)] p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-10 h-10 rounded-xl bg-teal-100 dark:bg-slate-700 flex items-center justify-center">
          <Activity className="w-5 h-5 text-teal-600" />
        </div>
        <h3 className="text-[11px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400">Recent Activity</h3>
      </div>
      {list.length === 0 ? (
        <div className="text-center py-6 text-sm text-slate-400">No recent activity</div>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-700/50">
          {list.map((ev, i) => {
            const cfg = iconMap[ev.type] || { Icon: Sparkles, bg: "bg-slate-100 dark:bg-slate-700", fg: "text-slate-500" };
            const Icon = cfg.Icon;
            return (
              <li
                key={`${ev.type}-${ev.target_id || i}-${i}`}
                onClick={() => ev.link && onEventClick && onEventClick(ev)}
                className={`flex items-center gap-3 py-2.5 ${ev.link ? "cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/40 -mx-2 px-2 rounded-lg" : ""} transition-colors`}
              >
                <div className={`w-9 h-9 rounded-full ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-4 h-4 ${cfg.fg}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-700 dark:text-slate-200 truncate">
                    <span className="font-semibold text-slate-900 dark:text-white">{ev.actor_name || "Someone"}</span>{" "}
                    <span className="text-slate-500">{ev.verb || "did something with"}</span>{" "}
                    <span className="font-medium text-indigo-600 dark:text-indigo-400">{ev.target || ""}</span>
                  </p>
                </div>
                <span className="text-[11px] text-slate-400 whitespace-nowrap">{relTime(ev.timestamp)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
});

// ============================================================
// AdminDashboard — main component
// ============================================================
const AdminDashboard = ({ adminMetrics, currentUser, visibleCards = [], businessId }) => {
  const navigate = useNavigate();
  if (!adminMetrics) return null;

  const teamSummary = adminMetrics.team_summary || {};
  const salesPerformance = adminMetrics.sales_performance || [];
  const teamPerformanceLeaderboard = adminMetrics.team_performance_leaderboard || [];
  const attendanceSummary = adminMetrics.attendance_summary || {};
  const recentActivity = adminMetrics.recent_activity || [];
  const overdueTasksCount = adminMetrics.overdue_tasks_count || 0;
  const dueTodayTasksCount = adminMetrics.due_today_tasks_count || 0;

  const isCardVisible = (cardId) => {
    if (!visibleCards || visibleCards.length === 0) return true;
    return visibleCards.includes(cardId);
  };

  // Alerts strip data
  const alerts = [];
  if ((adminMetrics.lost_leads || 0) > 0) {
    alerts.push({
      icon: XCircle,
      label: `${adminMetrics.lost_leads} leads lost this period`,
      onClick: () => navigate("/leads?status=lost"),
    });
  }
  if (overdueTasksCount > 0) {
    alerts.push({
      icon: CalendarClock,
      label: `${overdueTasksCount} overdue task${overdueTasksCount === 1 ? "" : "s"}`,
      onClick: () => navigate("/work-hub/tasks?filter=overdue"),
    });
  }
  if ((attendanceSummary.absent || 0) > 0) {
    alerts.push({
      icon: UserX,
      label: `${attendanceSummary.absent} absent today`,
      onClick: () => navigate("/attendance/records?filter=absent"),
    });
  }

  return (
    <div className="space-y-5" data-testid="admin-dashboard">
      {/* ============ Header (spec: title + subtitle only) ============ */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            Dashboard
            {adminMetrics.current_month && (
              <span className="text-xs font-normal text-slate-500 px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded-full">
                {adminMetrics.current_month}
              </span>
            )}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">Overview</p>
        </div>
      </div>

      {/* ============ ROW 1: KPI STRIP (6 tiles, 6-across desktop) ============ */}
      {(isCardVisible("total_revenue") || isCardVisible("expected_revenue") || isCardVisible("lead_performance")) && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-strip">
          {isCardVisible("total_revenue") && (
            <KpiCard icon={IndianRupee} label="Revenue" value={adminMetrics.total_revenue || 0} delta={adminMetrics.deltas?.total_revenue_pct} series={adminMetrics.sparkline} seriesKey="revenue" color="emerald" format="currency" />
          )}
          {isCardVisible("expected_revenue") && (
            <KpiCard icon={TrendingUp} label="Expected Rev" value={adminMetrics.expected_revenue || 0} delta={adminMetrics.deltas?.expected_revenue_pct} series={adminMetrics.sparkline} seriesKey="revenue" color="amber" format="currency" />
          )}
          {isCardVisible("lead_performance") && (
            <>
              <KpiCard icon={Users} label="Leads" value={adminMetrics.total_leads || 0} delta={adminMetrics.deltas?.total_leads_pct} series={adminMetrics.sparkline} seriesKey="leads" color="indigo" />
              <KpiCard icon={Trophy} label="Converted" value={adminMetrics.won_leads || 0} delta={adminMetrics.deltas?.won_leads_pct} series={adminMetrics.sparkline} seriesKey="leads" color="sky" />
              <KpiCard icon={PhoneCall} label="Follow-ups" value={adminMetrics.followup_pending || 0} delta={null} series={null} color="rose" />
              <KpiCard icon={Percent} label="Conversion" value={adminMetrics.conversion_rate || 0} delta={adminMetrics.deltas?.conversion_rate_pct} deltaSuffix="pp" series={adminMetrics.sparkline} seriesKey="leads" color="violet" format="percent" />
            </>
          )}
        </div>
      )}

      {/* ============ ROW 2: ALERTS STRIP (conditional) ============ */}
      {alerts.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {alerts.map((a, i) => {
            const AlertIcon = a.icon;
            return (
              <button
                key={i}
                type="button"
                onClick={a.onClick}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300 border border-rose-200/70 dark:border-rose-800/60 text-xs font-semibold hover:bg-rose-100 dark:hover:bg-rose-900/30 transition-colors"
              >
                <AlertIcon className="w-3.5 h-3.5" />
                <span>{a.label}</span>
                <ArrowUpRight className="w-3 h-3 opacity-70" />
              </button>
            );
          })}
        </div>
      )}

      {/* ============ ROW 3: 3-column summary grid ============ */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <AttendanceCard
          summary={attendanceSummary}
          onOpen={() => navigate("/attendance/records")}
        />
        <LeadsCard
          metrics={{
            new_today: adminMetrics.new_leads_today,
            new_leads_today: adminMetrics.new_leads_today,
            assigned_leads: adminMetrics.assigned_leads,
            unassigned_leads: adminMetrics.unassigned_leads,
            followup_pending: adminMetrics.followup_pending,
            sparkline: adminMetrics.sparkline,
          }}
          onOpen={() => navigate("/leads")}
        />
        <TasksCard
          summary={teamSummary}
          dueToday={dueTodayTasksCount}
          overdue={overdueTasksCount}
          onOpen={() => navigate("/work-hub/tasks")}
        />
      </div>

      {/* ============ ROW 4: 8/12 + 4/12 split ============ */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8">
          <SalesTeamPerformance
            rows={salesPerformance}
            onRowClick={(m) => m.user_id && navigate(`/leads?assigned_to=${m.user_id}`)}
          />
        </div>
        <div className="lg:col-span-4">
          <TeamLeaderboardCard
            rows={teamPerformanceLeaderboard}
            onOpen={() => navigate("/reports?tab=team")}
          />
        </div>
      </div>

      {/* ============ ROW 5: RECENT ACTIVITY FEED ============ */}
      <RecentActivityFeed
        events={recentActivity}
        onEventClick={(ev) => ev.link && navigate(ev.link)}
      />
    </div>
  );
};

export default AdminDashboard;
